import { useState, useEffect, useMemo } from 'react';
import {
    collection,
    getDocs,
    addDoc,
    deleteDoc,
    updateDoc,
    doc,
    writeBatch,
    serverTimestamp,
    query,
    orderBy,
    where
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
    DialogDescription
} from '../../components/ui/dialog';
import {
    Bell,
    Plus,
    Send,
    Users,
    Search,
    Loader2,
    Wallet,
    Calendar,
    Megaphone,
    Clock,
    RefreshCw,
    Trash2,
    Edit,
    CheckCircle2,
    AlertTriangle,
    Info,
    Shield,
    Filter,
    UserCheck,
    Play,
    Pause,
    Sparkles
} from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';

export default function AdminNotifications() {
    const { isAdmin } = useAuth();
    const [members, setMembers] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('broadcast');

    // Broadcast State
    const [broadcastOpen, setBroadcastOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [memberFilter, setMemberFilter] = useState('all');
    const [searchMember, setSearchMember] = useState('');
    const [notification, setNotification] = useState({
        title: '',
        message: '',
        type: 'info'
    });

    // Reminder State
    const [reminderOpen, setReminderOpen] = useState(false);
    const [savingReminder, setSavingReminder] = useState(false);
    const [newReminder, setNewReminder] = useState({
        name: '',
        title: '',
        message: '',
        type: 'contribution',
        frequency: 'monthly',
        dayOfMonth: 1,
        dayOfWeek: 1,
        targetAudience: 'all',
        isActive: true
    });

    // Delete State
    const [reminderToDelete, setReminderToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Templates
    const notificationTemplates = [
        {
            id: 'contribution_reminder',
            name: 'Rappel de cotisation',
            icon: Wallet,
            color: 'from-emerald-500 to-green-600',
            title: 'Rappel de cotisation mensuelle',
            message: 'Chers membres, nous vous rappelons que les cotisations du mois sont ouvertes. Merci de procéder à votre versement dès que possible.',
            type: 'contribution'
        },
        {
            id: 'event_reminder',
            name: 'Rappel d\'événement',
            icon: Calendar,
            color: 'from-purple-500 to-violet-600',
            title: 'Événement à venir',
            message: 'Un événement important approche ! Consultez le calendrier pour plus de détails et confirmez votre participation.',
            type: 'event'
        },
        {
            id: 'general_announcement',
            name: 'Annonce générale',
            icon: Megaphone,
            color: 'from-blue-500 to-cyan-600',
            title: 'Annonce du Dahira',
            message: '',
            type: 'info'
        },
        {
            id: 'urgent_alert',
            name: 'Alerte urgente',
            icon: AlertTriangle,
            color: 'from-red-500 to-rose-600',
            title: 'Alerte importante',
            message: '',
            type: 'alert'
        }
    ];

    const frequencyOptions = [
        { value: 'daily', label: 'Quotidien' },
        { value: 'weekly', label: 'Hebdomadaire' },
        { value: 'biweekly', label: 'Bi-hebdomadaire' },
        { value: 'monthly', label: 'Mensuel' },
        { value: 'quarterly', label: 'Trimestriel' }
    ];

    const audienceOptions = [
        { value: 'all', label: 'Tous les membres' },
        { value: 'active', label: 'Membres actifs uniquement' },
        { value: 'inactive', label: 'Membres inactifs' },
        { value: 'no_contribution', label: 'Sans cotisation ce mois' }
    ];

    const dayOptions = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: `${i + 1}` }));
    const weekDayOptions = [
        { value: 0, label: 'Dimanche' },
        { value: 1, label: 'Lundi' },
        { value: 2, label: 'Mardi' },
        { value: 3, label: 'Mercredi' },
        { value: 4, label: 'Jeudi' },
        { value: 5, label: 'Vendredi' },
        { value: 6, label: 'Samedi' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch members - utiliser une requête simple sans orderBy pour éviter les erreurs d'index
            const membersSnap = await getDocs(collection(db, 'users'));
            const membersData = membersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Trier côté client
            membersData.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
            setMembers(membersData);

            // Fetch reminders - utiliser une requête simple
            try {
                const remindersSnap = await getDocs(collection(db, 'scheduled_reminders'));
                const remindersData = remindersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Trier par date de création côté client
                remindersData.sort((a, b) => {
                    const dateA = a.createdAt?.toDate?.() || new Date(0);
                    const dateB = b.createdAt?.toDate?.() || new Date(0);
                    return dateB - dateA;
                });
                setReminders(remindersData);
            } catch (reminderError) {
                // La collection peut ne pas exister encore
                console.log('No reminders collection yet');
                setReminders([]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    // Filter members based on criteria
    const filteredMembers = useMemo(() => {
        return members.filter(m => {
            const matchesSearch = !searchMember ||
                m.fullName?.toLowerCase().includes(searchMember.toLowerCase()) ||
                m.email?.toLowerCase().includes(searchMember.toLowerCase());

            let matchesFilter = true;
            if (memberFilter === 'active') matchesFilter = m.isActive !== false && m.isApproved !== false;
            else if (memberFilter === 'inactive') matchesFilter = m.isActive === false;
            else if (memberFilter === 'pending') matchesFilter = m.isApproved === 'pending';

            return matchesSearch && matchesFilter;
        });
    }, [members, searchMember, memberFilter]);

    // Handle select all toggle
    useEffect(() => {
        if (selectAll) {
            setSelectedMembers(filteredMembers.map(m => m.id));
        } else if (selectedMembers.length === filteredMembers.length && filteredMembers.length > 0) {
            // Don't clear if we just toggled all on
        }
    }, [selectAll, filteredMembers]);

    const handleMemberToggle = (memberId) => {
        setSelectedMembers(prev =>
            prev.includes(memberId)
                ? prev.filter(id => id !== memberId)
                : [...prev, memberId]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedMembers([]);
        } else {
            setSelectedMembers(filteredMembers.map(m => m.id));
        }
        setSelectAll(!selectAll);
    };

    const applyTemplate = (template) => {
        setNotification({
            title: template.title,
            message: template.message,
            type: template.type
        });
    };

    const handleSendBroadcast = async () => {
        if (selectedMembers.length === 0) {
            toast.error('Veuillez sélectionner au moins un destinataire');
            return;
        }
        if (!notification.title || !notification.message) {
            toast.error('Veuillez remplir le titre et le message');
            return;
        }

        setSending(true);
        try {
            const batch = writeBatch(db);

            selectedMembers.forEach(memberId => {
                const notifRef = doc(collection(db, 'notifications'));
                batch.set(notifRef, {
                    userId: memberId,
                    title: notification.title,
                    message: notification.message,
                    type: notification.type,
                    read: false,
                    createdAt: serverTimestamp()
                });
            });

            await batch.commit();
            toast.success(`Notification envoyée à ${selectedMembers.length} membre(s)`);
            setBroadcastOpen(false);
            setNotification({ title: '', message: '', type: 'info' });
            setSelectedMembers([]);
            setSelectAll(false);
        } catch (error) {
            console.error('Error sending broadcast:', error);
            toast.error('Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    const handleSaveReminder = async () => {
        if (!newReminder.name || !newReminder.title || !newReminder.message) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setSavingReminder(true);
        try {
            await addDoc(collection(db, 'scheduled_reminders'), {
                ...newReminder,
                createdAt: serverTimestamp(),
                lastExecuted: null,
                nextExecution: calculateNextExecution(newReminder)
            });

            toast.success('Rappel programmé créé avec succès');
            setReminderOpen(false);
            setNewReminder({
                name: '',
                title: '',
                message: '',
                type: 'contribution',
                frequency: 'monthly',
                dayOfMonth: 1,
                dayOfWeek: 1,
                targetAudience: 'all',
                isActive: true
            });
            fetchData();
        } catch (error) {
            console.error('Error saving reminder:', error);
            toast.error('Erreur lors de la création');
        } finally {
            setSavingReminder(false);
        }
    };

    const calculateNextExecution = (reminder) => {
        const now = new Date();
        let next = new Date();

        switch (reminder.frequency) {
            case 'daily':
                next.setDate(now.getDate() + 1);
                next.setHours(9, 0, 0, 0);
                break;
            case 'weekly':
                next.setDate(now.getDate() + ((7 - now.getDay() + reminder.dayOfWeek) % 7 || 7));
                next.setHours(9, 0, 0, 0);
                break;
            case 'biweekly':
                next.setDate(now.getDate() + 14);
                next.setHours(9, 0, 0, 0);
                break;
            case 'monthly':
                next.setMonth(now.getMonth() + 1);
                next.setDate(reminder.dayOfMonth);
                next.setHours(9, 0, 0, 0);
                break;
            case 'quarterly':
                next.setMonth(now.getMonth() + 3);
                next.setDate(reminder.dayOfMonth);
                next.setHours(9, 0, 0, 0);
                break;
            default:
                break;
        }

        return next;
    };

    const handleToggleReminder = async (reminder) => {
        try {
            await updateDoc(doc(db, 'scheduled_reminders', reminder.id), {
                isActive: !reminder.isActive
            });
            toast.success(reminder.isActive ? 'Rappel désactivé' : 'Rappel activé');
            fetchData();
        } catch (error) {
            console.error('Error toggling reminder:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleDeleteReminder = async () => {
        if (!reminderToDelete) return;
        setDeleting(true);
        try {
            await deleteDoc(doc(db, 'scheduled_reminders', reminderToDelete.id));
            toast.success('Rappel supprimé');
            fetchData();
        } catch (error) {
            console.error('Error deleting reminder:', error);
            toast.error('Erreur lors de la suppression');
        } finally {
            setDeleting(false);
            setReminderToDelete(null);
        }
    };

    const handleExecuteReminder = async (reminder) => {
        setSending(true);
        try {
            // Get target members based on audience
            let targetMembers = members.filter(m => m.isApproved !== false && m.isApproved !== 'pending');

            if (reminder.targetAudience === 'active') {
                targetMembers = targetMembers.filter(m => m.isActive !== false);
            } else if (reminder.targetAudience === 'inactive') {
                targetMembers = targetMembers.filter(m => m.isActive === false);
            }

            // Send notifications
            const batch = writeBatch(db);
            targetMembers.forEach(member => {
                const notifRef = doc(collection(db, 'notifications'));
                batch.set(notifRef, {
                    userId: member.id,
                    title: reminder.title,
                    message: reminder.message,
                    type: reminder.type,
                    read: false,
                    createdAt: serverTimestamp()
                });
            });

            // Update last executed
            const reminderRef = doc(db, 'scheduled_reminders', reminder.id);
            batch.update(reminderRef, {
                lastExecuted: serverTimestamp(),
                nextExecution: calculateNextExecution(reminder)
            });

            await batch.commit();
            toast.success(`Rappel exécuté - ${targetMembers.length} notifications envoyées`);
            fetchData();
        } catch (error) {
            console.error('Error executing reminder:', error);
            toast.error('Erreur lors de l\'exécution');
        } finally {
            setSending(false);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'contribution': return Wallet;
            case 'event': return Calendar;
            case 'alert': return AlertTriangle;
            default: return Info;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'contribution': return 'text-emerald-500 bg-emerald-500/10';
            case 'event': return 'text-purple-500 bg-purple-500/10';
            case 'alert': return 'text-red-500 bg-red-500/10';
            default: return 'text-blue-500 bg-blue-500/10';
        }
    };

    if (!isAdmin) {
        return (
            <div className="text-center py-12">
                <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
                <p className="text-muted-foreground">Cette page est réservée aux administrateurs</p>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-notifications-page">
            {/* Hero Header */}
            <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-5 md:p-8 text-white">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiIGN4PSIyMCIgY3k9IjIwIiByPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl hidden md:block" />

                <div className="relative z-10 flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Megaphone className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                            <span className="text-primary text-xs md:text-sm font-medium">Centre de diffusion</span>
                        </div>
                        <h1 className="text-2xl md:text-4xl font-bold tracking-tight">Notifications Admin</h1>
                        <p className="text-slate-400 max-w-md text-sm hidden md:block">
                            Envoyez des notifications groupées et configurez des rappels automatiques
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
                            <DialogTrigger asChild>
                                <Button className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 gap-2">
                                    <Send className="h-4 w-4" />
                                    <span className="hidden md:inline">Diffusion</span>
                                </Button>
                            </DialogTrigger>
                        </Dialog>

                        <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="bg-white/5 border-white/10 hover:bg-white/10 text-white gap-2">
                                    <Clock className="h-4 w-4" />
                                    <span className="hidden md:inline">Nouveau rappel</span>
                                </Button>
                            </DialogTrigger>
                        </Dialog>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-2 md:gap-4">
                <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
                    <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-primary/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                    <CardContent className="p-3 md:p-6 relative">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] md:text-sm text-muted-foreground">Membres</p>
                                <p className="text-xl md:text-3xl font-bold">{members.filter(m => m.isApproved !== false).length}</p>
                            </div>
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                                <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
                    <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                    <CardContent className="p-3 md:p-6 relative">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] md:text-sm text-muted-foreground">Rappels actifs</p>
                                <p className="text-xl md:text-3xl font-bold text-emerald-600">{reminders.filter(r => r.isActive).length}</p>
                            </div>
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg">
                                <RefreshCw className="h-5 w-5 md:h-6 md:w-6 text-white" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
                    <div className="absolute -right-4 -top-4 w-20 h-20 md:w-24 md:h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                    <CardContent className="p-3 md:p-6 relative">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] md:text-sm text-muted-foreground">Total rappels</p>
                                <p className="text-xl md:text-3xl font-bold text-amber-600">{reminders.length}</p>
                            </div>
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg">
                                <Clock className="h-5 w-5 md:h-6 md:w-6 text-white" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content Tabs */}
            <Tabs defaultValue="broadcast" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-muted/50 p-1">
                    <TabsTrigger value="broadcast" className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow">
                        <Send className="h-4 w-4" />
                        Diffusion rapide
                    </TabsTrigger>
                    <TabsTrigger value="reminders" className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow">
                        <RefreshCw className="h-4 w-4" />
                        Rappels programmés
                    </TabsTrigger>
                </TabsList>

                {/* Broadcast Tab */}
                <TabsContent value="broadcast" className="space-y-6 mt-6">
                    {/* Template Cards */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4">Modèles de notification</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {notificationTemplates.map((template) => {
                                const Icon = template.icon;
                                return (
                                    <Card
                                        key={template.id}
                                        className="cursor-pointer hover:shadow-lg transition-all duration-300 group border-2 hover:border-primary/30"
                                        onClick={() => {
                                            applyTemplate(template);
                                            setBroadcastOpen(true);
                                        }}
                                    >
                                        <CardContent className="p-4 text-center">
                                            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${template.color} flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform`}>
                                                <Icon className="h-6 w-6 text-white" />
                                            </div>
                                            <p className="font-semibold text-sm">{template.name}</p>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quick Send Section */}
                    <Card className="border-slate-200 dark:border-slate-800">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-primary" />
                                Envoi rapide
                            </CardTitle>
                            <CardDescription>
                                Envoyez une notification personnalisée à tous les membres actifs
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Titre</Label>
                                <Input
                                    value={notification.title}
                                    onChange={(e) => setNotification({ ...notification, title: e.target.value })}
                                    placeholder="Ex: Rappel de cotisation du mois"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Message</Label>
                                <Textarea
                                    value={notification.message}
                                    onChange={(e) => setNotification({ ...notification, message: e.target.value })}
                                    placeholder="Écrivez votre message..."
                                    rows={4}
                                />
                            </div>
                            <div className="flex gap-4">
                                <Button
                                    onClick={() => {
                                        setSelectedMembers(members.filter(m => m.isActive !== false && m.isApproved !== false).map(m => m.id));
                                        setBroadcastOpen(true);
                                    }}
                                    className="flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                                    disabled={!notification.title || !notification.message}
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    Envoyer à tous les actifs
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setBroadcastOpen(true)}
                                    disabled={!notification.title || !notification.message}
                                >
                                    <Filter className="h-4 w-4 mr-2" />
                                    Sélectionner les destinataires
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Reminders Tab */}
                <TabsContent value="reminders" className="space-y-6 mt-6">
                    <Card className="border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="flex items-center justify-center py-16">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : reminders.length > 0 ? (
                                <Table>
                                    <TableHeader className="bg-slate-50/50 dark:bg-slate-900/50">
                                        <TableRow className="hover:bg-transparent border-0">
                                            <TableHead className="py-4 px-6 font-bold">Rappel</TableHead>
                                            <TableHead className="font-bold">Fréquence</TableHead>
                                            <TableHead className="font-bold">Audience</TableHead>
                                            <TableHead className="font-bold">Statut</TableHead>
                                            <TableHead className="font-bold">Prochaine exécution</TableHead>
                                            <TableHead className="w-32"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {reminders.map((reminder) => {
                                            const TypeIcon = getTypeIcon(reminder.type);
                                            return (
                                                <TableRow key={reminder.id} className="group hover:bg-slate-50/80 dark:hover:bg-slate-900/40">
                                                    <TableCell className="py-4 px-6">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getTypeColor(reminder.type)}`}>
                                                                <TypeIcon className="h-5 w-5" />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold">{reminder.name}</p>
                                                                <p className="text-xs text-muted-foreground">{reminder.title}</p>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary">
                                                            {frequencyOptions.find(f => f.value === reminder.frequency)?.label}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {audienceOptions.find(a => a.value === reminder.targetAudience)?.label}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-2 h-2 rounded-full ${reminder.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                                                            <span className={`text-sm font-medium ${reminder.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                                {reminder.isActive ? 'Actif' : 'Inactif'}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {reminder.nextExecution ? (
                                                            <span className="text-sm text-muted-foreground">
                                                                {new Date(reminder.nextExecution.toDate?.() || reminder.nextExecution).toLocaleDateString('fr-FR')}
                                                            </span>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => handleExecuteReminder(reminder)}
                                                                disabled={sending}
                                                                title="Exécuter maintenant"
                                                            >
                                                                <Play className="h-4 w-4 text-primary" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => handleToggleReminder(reminder)}
                                                                title={reminder.isActive ? 'Désactiver' : 'Activer'}
                                                            >
                                                                {reminder.isActive ? (
                                                                    <Pause className="h-4 w-4 text-amber-500" />
                                                                ) : (
                                                                    <Play className="h-4 w-4 text-emerald-500" />
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 text-red-500 hover:text-red-600"
                                                                onClick={() => setReminderToDelete(reminder)}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="py-16 text-center">
                                    <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                                        <Clock className="h-10 w-10 text-muted-foreground" />
                                    </div>
                                    <h3 className="text-xl font-semibold mb-2">Aucun rappel programmé</h3>
                                    <p className="text-muted-foreground mb-6">
                                        Créez votre premier rappel automatique
                                    </p>
                                    <Button onClick={() => setReminderOpen(true)} className="gap-2">
                                        <Plus className="h-4 w-4" />
                                        Créer un rappel
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Broadcast Dialog */}
            <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send className="h-5 w-5 text-primary" />
                            Envoyer une notification groupée
                        </DialogTitle>
                        <DialogDescription>
                            Sélectionnez les destinataires et personnalisez votre message
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-auto space-y-6 py-4">
                        {/* Notification Content */}
                        <div className="space-y-4 pb-4 border-b">
                            <div className="space-y-2">
                                <Label>Titre de la notification *</Label>
                                <Input
                                    value={notification.title}
                                    onChange={(e) => setNotification({ ...notification, title: e.target.value })}
                                    placeholder="Ex: Rappel important"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Message *</Label>
                                <Textarea
                                    value={notification.message}
                                    onChange={(e) => setNotification({ ...notification, message: e.target.value })}
                                    placeholder="Écrivez votre message..."
                                    rows={3}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select value={notification.type} onValueChange={(v) => setNotification({ ...notification, type: v })}>
                                    <SelectTrigger className="w-48">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="info">Information</SelectItem>
                                        <SelectItem value="contribution">Cotisation</SelectItem>
                                        <SelectItem value="event">Événement</SelectItem>
                                        <SelectItem value="alert">Alerte</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Member Selection */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="font-semibold">Sélectionner les destinataires</h4>
                                <Badge variant="secondary" className="text-primary">
                                    {selectedMembers.length} sélectionné(s)
                                </Badge>
                            </div>

                            <div className="flex gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        value={searchMember}
                                        onChange={(e) => setSearchMember(e.target.value)}
                                        placeholder="Rechercher un membre..."
                                        className="pl-10"
                                    />
                                </div>
                                <Select value={memberFilter} onValueChange={setMemberFilter}>
                                    <SelectTrigger className="w-48">
                                        <Filter className="h-4 w-4 mr-2" />
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="active">Actifs</SelectItem>
                                        <SelectItem value="inactive">Inactifs</SelectItem>
                                        <SelectItem value="pending">En attente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="border rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-900 border-b sticky top-0">
                                    <Checkbox
                                        checked={selectAll}
                                        onCheckedChange={handleSelectAll}
                                        id="select-all"
                                    />
                                    <Label htmlFor="select-all" className="font-medium cursor-pointer">
                                        Sélectionner tout ({filteredMembers.length})
                                    </Label>
                                </div>
                                {filteredMembers.map((member) => (
                                    <div
                                        key={member.id}
                                        className={`flex items-center gap-3 p-3 border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer transition-colors ${selectedMembers.includes(member.id) ? 'bg-primary/5' : ''
                                            }`}
                                        onClick={() => handleMemberToggle(member.id)}
                                    >
                                        <Checkbox
                                            checked={selectedMembers.includes(member.id)}
                                            onCheckedChange={() => handleMemberToggle(member.id)}
                                        />
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                                            {member.fullName?.charAt(0) || 'U'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{member.fullName}</p>
                                            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                                        </div>
                                        {member.isActive !== false && member.isApproved !== false ? (
                                            <Badge variant="secondary" className="text-emerald-600 bg-emerald-50">Actif</Badge>
                                        ) : member.isApproved === 'pending' ? (
                                            <Badge variant="secondary" className="text-amber-600 bg-amber-50">En attente</Badge>
                                        ) : (
                                            <Badge variant="secondary" className="text-slate-500">Inactif</Badge>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="pt-4 border-t">
                        <Button variant="outline" onClick={() => setBroadcastOpen(false)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSendBroadcast}
                            disabled={sending || selectedMembers.length === 0 || !notification.title || !notification.message}
                            className="gap-2"
                        >
                            {sending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" />
                                    Envoyer à {selectedMembers.length} membre(s)
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reminder Dialog */}
            <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-primary" />
                            Créer un rappel programmé
                        </DialogTitle>
                        <DialogDescription>
                            Configurez un rappel automatique récurrent
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom du rappel *</Label>
                            <Input
                                value={newReminder.name}
                                onChange={(e) => setNewReminder({ ...newReminder, name: e.target.value })}
                                placeholder="Ex: Rappel cotisation mensuelle"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Titre de la notification *</Label>
                            <Input
                                value={newReminder.title}
                                onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                                placeholder="Ex: Rappel de cotisation"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Message *</Label>
                            <Textarea
                                value={newReminder.message}
                                onChange={(e) => setNewReminder({ ...newReminder, message: e.target.value })}
                                placeholder="Contenu du message..."
                                rows={3}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select value={newReminder.type} onValueChange={(v) => setNewReminder({ ...newReminder, type: v })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="contribution">Cotisation</SelectItem>
                                        <SelectItem value="event">Événement</SelectItem>
                                        <SelectItem value="info">Information</SelectItem>
                                        <SelectItem value="alert">Alerte</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Fréquence</Label>
                                <Select value={newReminder.frequency} onValueChange={(v) => setNewReminder({ ...newReminder, frequency: v })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {frequencyOptions.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {newReminder.frequency === 'weekly' && (
                            <div className="space-y-2">
                                <Label>Jour de la semaine</Label>
                                <Select value={String(newReminder.dayOfWeek)} onValueChange={(v) => setNewReminder({ ...newReminder, dayOfWeek: parseInt(v) })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {weekDayOptions.map(opt => (
                                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {(newReminder.frequency === 'monthly' || newReminder.frequency === 'quarterly') && (
                            <div className="space-y-2">
                                <Label>Jour du mois</Label>
                                <Select value={String(newReminder.dayOfMonth)} onValueChange={(v) => setNewReminder({ ...newReminder, dayOfMonth: parseInt(v) })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {dayOptions.map(opt => (
                                            <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Audience cible</Label>
                            <Select value={newReminder.targetAudience} onValueChange={(v) => setNewReminder({ ...newReminder, targetAudience: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {audienceOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl">
                            <div>
                                <p className="font-medium">Activer immédiatement</p>
                                <p className="text-sm text-muted-foreground">Le rappel sera planifié dès sa création</p>
                            </div>
                            <Switch
                                checked={newReminder.isActive}
                                onCheckedChange={(v) => setNewReminder({ ...newReminder, isActive: v })}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReminderOpen(false)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSaveReminder}
                            disabled={savingReminder || !newReminder.name || !newReminder.title || !newReminder.message}
                            className="gap-2"
                        >
                            {savingReminder ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Création...
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="h-4 w-4" />
                                    Créer le rappel
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Reminder Confirmation */}
            <ConfirmDeleteDialog
                open={!!reminderToDelete}
                onOpenChange={(open) => !open && setReminderToDelete(null)}
                onConfirm={handleDeleteReminder}
                title="Supprimer ce rappel ?"
                description="Cette action est irréversible. Le rappel programmé sera définitivement supprimé."
                itemName={reminderToDelete?.name}
                confirmText="Supprimer le rappel"
                loading={deleting}
                variant="danger"
            />
        </div>
    );
}
